import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Feature, SuggestedPrices } from '../../../interfaces/publications';

const addPointsInText = (text: string | number | undefined): string => {
    if (!text) return '';
    return text.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const boldBetweenAsterisks = (text: string | undefined): string => {
    if (!text) return '';
    return text.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
};

export const usePublicationDetail = ({ publication }: { publication: any }) => {
    const navigate = useNavigate();

    const isBonus = useMemo(() => {
        if (publication?.detail?.bonus_info) {
            return Boolean(publication?.detail?.bonus_info?.bonus);
        }
        return false;
    }, [publication]);

    const formData = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('formData') || '{}');
        } catch (e) {
            return {};
        }
    }, []);

    const selectedFeatures: Feature[] = useMemo(() => {
        return publication?.additional_characters?.flatMap((category: any) =>
            (category.value || [])
                .filter((item: any) => item.value === true)
                .map((item: any) => ({ name: item.name, code: item.code }))
        ) || [];
    }, [publication]);

    const getIndicatorPositionFromSuggested = (
        carPrice: string = "0",
        suggested: SuggestedPrices
    ): number => {
        const numPrice = parseInt(carPrice || "0");
        const minPrice = parseInt(suggested.retake || "0");
        const mediumPrice = parseInt(suggested.sale || "0");
        const maxPrice = parseInt(suggested.publication || "0");

        if (!minPrice || !mediumPrice || !maxPrice) return 50;
        if (numPrice === mediumPrice) return 50;

        if (numPrice < mediumPrice) {
            const rangeToMedium = mediumPrice - minPrice;
            const position = ((numPrice - minPrice) / rangeToMedium) * 50;
            return Math.max(0, Math.min(50, position));
        } else {
            const rangeFromMedium = maxPrice - mediumPrice;
            const position = 50 + ((numPrice - mediumPrice) / rangeFromMedium) * 50;
            return Math.min(100, position);
        }
    };

    const getPriceDifferenceMessage = () => {
        const price = parseInt(publication?.price || "0");
        const suggested = parseInt(publication?.vehicle_data?.suggested_price?.suggested_price || "0");

        if (!price || !suggested) return "";

        if (price === suggested) {
            return `Este auto coincide con el precio del mercado. Según precio, kilometraje y condición.`;
        }

        const diff = Math.abs(suggested - price);
        const formattedDiff = `$${diff.toLocaleString("es-AR")}`;
        const direction = price < suggested ? "por debajo" : "por encima";

        return `Este auto está ${formattedDiff} ${direction} del precio del mercado. Según precio, kilometraje y condición.`;
    };

    const getIndicatorPosition = (): number => {
        const price = parseInt(publication?.price || "0");
        const retake = parseInt(publication?.suggested_prices?.retake || "0");
        const sale = parseInt(publication?.suggested_prices?.sale || "0");
        const publicationPrice = parseInt(publication?.suggested_prices?.publication || "0");

        if (!retake || !sale || !publicationPrice) return 50;
        if (price === sale) return 50;

        let position = 50;

        if (price < sale) {
            const range = sale - retake;
            position = ((price - retake) / range) * 50;
        } else {
            const range = publicationPrice - sale;
            position = 50 + ((price - sale) / range) * 50;
        }

        return Math.max(6, Math.min(94, position));
    };

    const getIndicatorPositionPublication = (): number => {
        const price = parseInt(publication?.price || "0");
        const retake = parseInt(publication?.vehicle_data?.suggested_price?.min_price || "0");
        const sale = parseInt(publication?.vehicle_data?.suggested_price?.suggested_price || "0");
        const publicationPrice = parseInt(publication?.vehicle_data?.suggested_price?.max_price || "0");

        if (!retake || !sale || !publicationPrice) return 50;
        if (price === sale) return 50;

        let position = 50;

        if (price < sale) {
            const range = sale - retake;
            position = ((price - retake) / range) * 50;
        } else {
            const range = publicationPrice - sale;
            position = 50 + ((price - sale) / range) * 50;
        }

        return Math.max(6, Math.min(94, position));
    };
    
    const getPriceCategory = (): "good" | "fair" | "high" => {
        const price = parseInt(publication?.price || "0");
        const retake = parseInt(publication?.suggested_prices?.retake || "0");
        const sale = parseInt(publication?.suggested_prices?.sale || "0");
        const publicationPrice = parseInt(publication?.suggested_prices?.publication || "0");

      
        if (!retake || !sale || !publicationPrice) return "fair";

        if (price < (sale)) {
            return "good";
        }

        if (price > (sale + 1000000)) {
            return "high";
        }

        return "fair";
    };

    const getPriceCategoryPublication = (): "good" | "fair" | "high" => {
        const price = parseInt(publication?.price || "0");
        const retake = parseInt(publication?.vehicle_data?.suggested_price?.min_price || "0");
        const sale = parseInt(publication?.vehicle_data?.suggested_price?.suggested_price || "0");
        const publicationPrice = parseInt(publication?.vehicle_data?.suggested_price?.max_price || "0");

        if (!retake || !sale || !publicationPrice) return "fair";

        if (price < (sale)) {
            return "good";
        }

        if (price > (sale + 1000000)) {
            return "high";
        }

        return "fair";
    };

    const hasValidSuggestedPrices = useMemo(() => {
        const suggested = publication?.suggested_prices;
        return suggested &&
            [suggested.sale, suggested.retake, suggested.publication].every(
                (val) => parseInt(val) !== 0
            );
    }, [publication]);

    

    const hasValidSuggestedPricesPublication = useMemo(() => {
        return publication?.vehicle_data?.suggested_price &&
            [publication?.vehicle_data?.suggested_price?.suggested_price, publication?.vehicle_data?.suggested_price?.min_price, publication?.vehicle_data?.suggested_price?.max_price].every(
                (val) => parseInt(val) !== 0
            );
    }, [publication]);


    return {
        isBonus,
        formData,
        selectedFeatures,
        getIndicatorPositionFromSuggested,
        getPriceDifferenceMessage,
        getIndicatorPosition,
        getIndicatorPositionPublication,
        getPriceCategory,
        getPriceCategoryPublication,
        hasValidSuggestedPrices,
        hasValidSuggestedPricesPublication,
        navigate,
        addPointsInText,
        boldBetweenAsterisks,
        handleDealerLogoClick: () => navigate('/concesionaria'),
        onLinzeCheckClick: () => console.log('LinzeCheck clicked')
    };
};
